'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCart } from '@/context/cart-context';
import ArtworkUpload from '@/components/product/artwork-upload';
import type { BannerProduct } from '@/data/banner-products';

interface BannerOrderFormProps {
  banner: BannerProduct;
}

export default function BannerOrderForm({ banner }: BannerOrderFormProps) {
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [artworkUrl, setArtworkUrl] = useState('');
  const [artworkFilename, setArtworkFilename] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState('');
  const { addItem } = useCart();

  const selectedSize = banner.sizes[selectedSizeIndex];
  const hasShopifyVariant = Boolean(selectedSize.variantId);
  const total = selectedSize.price * quantity;

  const handleAddToCart = async () => {
    if (!selectedSize.variantId) return;

    setIsAdding(true);
    setError('');

    try {
      await addItem(selectedSize.variantId, quantity, [
        { key: 'Banner Type', value: banner.title },
        { key: 'Size', value: selectedSize.label },
        { key: 'Height', value: selectedSize.height },
        { key: 'Width', value: selectedSize.width },
        ...(artworkUrl ? [{ key: 'Artwork', value: artworkUrl }] : []),
        ...(artworkFilename ? [{ key: 'Artwork Filename', value: artworkFilename }] : []),
      ]);
    } catch {
      setError('Could not add this banner to cart. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div id="order" className="min-w-0 w-full max-w-[calc(100vw-2rem)] border border-[var(--color-border)] bg-white p-6 lg:sticky lg:top-36 lg:max-w-none lg:p-8">
      <div className="mb-6">
        <p className="text-sm font-semibold uppercase tracking-widest text-[var(--color-text-muted)] mb-2">
          Order Online
        </p>
        <h2 className="text-2xl font-bold">Choose your banner</h2>
      </div>

      <div className="space-y-6">
        <div>
          <label htmlFor="banner-size" className="block text-sm font-medium uppercase tracking-wider mb-3">
            Size
          </label>
          <select
            id="banner-size"
            value={selectedSizeIndex}
            onChange={(event) => setSelectedSizeIndex(Number(event.target.value))}
            className="w-full border border-[var(--color-border)] bg-white px-4 py-3 text-sm focus:border-black focus:outline-none"
          >
            {banner.sizes.map((size, index) => (
              <option key={size.label} value={index}>
                {size.label} - £{size.price}
              </option>
            ))}
          </select>
        </div>

        <div>
          <span className="block text-sm font-medium uppercase tracking-wider mb-3">Quantity</span>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => setQuantity((current) => Math.max(1, current - 1))}
              disabled={quantity <= 1}
              className="h-12 w-12 border border-[var(--color-border)] text-lg disabled:opacity-50"
              aria-label="Decrease quantity"
            >
              -
            </button>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(event) => setQuantity(Math.max(1, Number(event.target.value) || 1))}
              className="h-12 w-20 border-y border-[var(--color-border)] text-center text-sm focus:outline-none"
              aria-label="Quantity"
            />
            <button
              type="button"
              onClick={() => setQuantity((current) => current + 1)}
              className="h-12 w-12 border border-[var(--color-border)] text-lg"
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
        </div>

        <ArtworkUpload
          artworkUrl={artworkUrl}
          artworkFilename={artworkFilename}
          guidanceImageSrc="/images/banner-printing/artwork-vs-mockup-banner.png"
          guidanceImageAlt="Upload the flat artwork file, not a banner mock-up preview."
          onUpload={(url, filename) => {
            setArtworkUrl(url);
            setArtworkFilename(filename);
          }}
          onRemove={() => {
            setArtworkUrl('');
            setArtworkFilename('');
          }}
        />

        <div className="border-t border-[var(--color-border)] pt-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-[var(--color-text-secondary)]">
              {selectedSize.label} x {quantity}
            </span>
            <span className="text-2xl font-bold">£{total}</span>
          </div>

          {hasShopifyVariant ? (
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={isAdding}
              className="w-full bg-brand-blue px-6 py-4 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-blue-hover disabled:bg-gray-300 disabled:text-gray-500"
            >
              {isAdding ? 'Adding...' : 'Add to Cart and Checkout'}
            </button>
          ) : (
            <div className="space-y-3">
              <button
                type="button"
                disabled
                className="w-full bg-gray-300 px-6 py-4 text-sm font-semibold uppercase tracking-wider text-gray-600"
              >
                Online Payment Setup Pending
              </button>
              <Link
                href="/contact"
                className="block w-full border border-brand-blue text-brand-blue px-6 py-4 text-center text-sm font-semibold uppercase tracking-wider transition-colors hover:bg-brand-blue hover:text-white"
              >
                Order by Quote
              </Link>
            </div>
          )}

          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          <ul className="mt-5 space-y-2 text-xs leading-relaxed text-[var(--color-text-muted)]">
            {[
              'Full-colour banner printing',
              'Eyelets included',
              'Upload artwork before checkout',
              'Local collection available from Birmingham',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="text-green-600" aria-hidden="true">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
